import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  Text,
  Avatar,
  Box,
  Divider,
  IconButton,
} from '@chakra-ui/react';

import * as FiIcons from 'react-icons/fi';

import { toggleSidebar } from '../../../store/sidebar/sidebarSlice';
import { getNotifications } from '../../../store/notifications/notificationsSlice';
import { logout } from '../../../store/auth/authSlice';

import { NavbarWrapper } from './NavbarStyle';

import theme from '../../global/theme';
import flagSA from '../../../assets/images/countries/السعوديه.png';
import EuFlagIcon from '../../shared/EuFlagIcon';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const cart = useSelector(state => state.cart);
  const sidebar = useSelector(state => state.sidebar);

  const [searchParams] = useSearchParams();

  const [page] = useState(1);
  const [query] = useState('');

  const defaultFilter = {};
  if (searchParams.get('type')) {
    defaultFilter.type = searchParams.get('type');
  }

  const [filter] = useState(JSON.stringify(defaultFilter));

  useEffect(() => {
    dispatch(getNotifications({ page, query, filter }));
  }, [dispatch, page, query, filter]);

  const ActionIcon = ({ to, icon, count, label }) => (
    <Box as={Link} to={to} className="nav-action" aria-label={label}>
      <Box className="nav-action-icon">{icon}</Box>
      {count > 0 && (
        <Flex className="nav-badge">
          {count}
        </Flex>
      )}
    </Box>
  );

  return (
    <NavbarWrapper className={`${sidebar.isNotOpened ? 'active' : ''}`}>
      <Flex align="center" justify="space-between" gap={4}>
        <Flex align="center" gap={3}>
          <IconButton
            aria-label="toggle sidebar"
            icon={<FiIcons.FiAlignRight size={22} />}
            className="menu-toggle-btn"
            onClick={() => dispatch(toggleSidebar())}
          />

          <Box className="navbar-title-box">
            <Text className="navbar-title">
              {t('layout.sidebar.dashboard')}
            </Text>
            <Text className="navbar-subtitle">
              مرحباً بعودتك، {auth.user?.name && auth.user.name.slice(0, 18)}
            </Text>
          </Box>
        </Flex>

        <Flex align="center" gap={{ base: 2, md: 3 }}>
          <ActionIcon
            to="/"
            icon={<FiIcons.FiExternalLink size={18} />}
            count={cart.items.length}
            label="website"
          />

          <Button
            type="button"
            className="lang-btn"
            onClick={() =>
              i18n.changeLanguage(i18n.language === 'ar' ? 'en' : 'ar')
            }
          >
            {i18n.language === 'ar' ? (
              <EuFlagIcon style={{ width: '18px', height: '13px', borderRadius: '2px', flexShrink: 0 }} />
            ) : (
              <Box
                as="img"
                src={flagSA}
                alt="AR"
                w="18px"
                h="13px"
                borderRadius="2px"
                objectFit="cover"
                flexShrink={0}
              />
            )}
            <Text className="lang-text">
              {i18n.language === 'ar' ? 'EN' : 'AR'}
            </Text>
          </Button>

          <Menu>
            <MenuButton
              as={Button}
              className="profile-btn"
              rightIcon={<FiIcons.FiChevronDown size={16} />}
            >
              <Flex align="center" gap={3}>
                <Avatar
                  name={auth.user?.name}
                  src={auth.user?.avatar}
                  size="sm"
                  bg={theme.primary}
                  color={theme.light}
                />

                <Box className="profile-text-box">
                  <Text className="profile-name">
                    {auth.user?.name && auth.user.name.slice(0, 18)}
                  </Text>
                  <Text className="profile-role">
                    {auth.user?.role?.name ?? auth.user?.type}
                  </Text>
                </Box>
              </Flex>
            </MenuButton>

            <MenuList className="profile-menu" minW="240px">
              <Box p={4}>
                <Flex align="center" gap={3} mb={3}>
                  <Avatar
                    name={auth.user?.name}
                    src={auth.user?.avatar}
                    size="md"
                    bg={theme.primary}
                    color={theme.light}
                  />
                  <Box>
                    <Text className="menu-user-name">
                      {auth.user?.name}
                    </Text>
                  </Box>
                </Flex>

                <Divider my={3} />

                <Button
                  type="button"
                  className="logout-btn"
                  onClick={() => dispatch(logout())}
                >
                  <FiIcons.FiLogOut size={16} />
                  <Text>{t('layout.navbar.logout')}</Text>
                </Button>
              </Box>
            </MenuList>
          </Menu>
        </Flex>
      </Flex>
    </NavbarWrapper>
  );
};

export default Navbar;